import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { MaybeLink } from './Crumbs'
import styles from './Feed.module.css'

/**
 * A feed card's sentence (to the review's thread). On a phone inside the community feed it is
 * clamped to four lines by CSS (`.mobileCard .quote`); after mount, when the clamp actually cut
 * the text, an inline "More" shows it whole. The server HTML always carries the full sentence —
 * the clamp is presentation only; on desktop nothing is clamped, so "More" never appears.
 * `oneLine` (a condensed row, `FeedRow condensed`): one ellipsised line at every width and no
 * "More" — the link to the thread is the way to the rest; the full sentence stays in the HTML.
 */
const FeedQuote: React.VFC<{ href: string | null; text: string; oneLine?: boolean }> = ({
  href,
  text,
  oneLine = false,
}) => {
  const box = useRef<HTMLQuoteElement>(null)
  const [clamped, setClamped] = useState(false)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const el = box.current
    if (!el || open || oneLine) {
      return undefined
    }
    const measure = (): void => setClamped(el.scrollHeight > el.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    return () => watch.disconnect()
  }, [open, oneLine, text])
  return (
    <blockquote
      ref={box}
      className={clsx(styles.quote, open && styles.quoteOpen, oneLine && styles.quoteOneLine)}
    >
      <MaybeLink href={href} className={styles.quoteLink}>
        {`“${text}”`}
      </MaybeLink>
      {clamped && !open && (
        <button type="button" className={styles.quoteMore} onClick={() => setOpen(true)}>
          {'More'}
        </button>
      )}
    </blockquote>
  )
}

export default FeedQuote
